import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addLookupHistoryItemDescription, removeFromLookupHistory } from '../services/lookupHistoryService'
import { CIS_USER_ID_CONTEXT, MRN_ID_CONTEXT, USER_ID_CONTEXT } from '../components/app/LookupContext'
import useNotifications from './useNotifications'
import getUserSummary from '../services/userService'

export default function useUser(userId, context, forward = true) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [currentUser, setCurrentUser] = useState()
  const typeContextMatch = context || USER_ID_CONTEXT

  const fetchCurrentUser = useCallback(() => {
    setCurrentUser(null)

    const setMetadata = (metadata) => {
      const oldMetadata = metadata
      const items = []
      if (oldMetadata.contributorId) {
        items.push({
          ...oldMetadata.contributorId,
          label: t('contributorId', 'Contributor ID'),
          showLabel: true,
          field: 'contributorId',
        })
      }
      if (oldMetadata.pid) {
        items.push({ ...oldMetadata.pid, label: t('pid', 'PID'), showLabel: true, field: 'pid' })
      }
      if (oldMetadata.mrn) {
        items.push({ ...oldMetadata.mrn, label: t('mrn', 'MRN'), showLabel: true, field: 'mrn' })
      }
      metadata = { header: oldMetadata.header, items, badges: metadata.badges }
      return metadata
    }

    if (userId) {
      getUserSummary(userId, context, forward)
        .then(async (results) => {
          const data = results.data
          data.metadata = setMetadata(data.metadata)
          data.input = { userId, context }
          setCurrentUser(data)
          switch (typeContextMatch) {
            case CIS_USER_ID_CONTEXT:
            case MRN_ID_CONTEXT:
              addLookupHistoryItemDescription(typeContextMatch, userId, data.metadata.header)
              break
            default:
              addLookupHistoryItemDescription(USER_ID_CONTEXT, userId, data.metadata.header)
          }
        })
        .catch((err) => {
          if (err.response && err.response.status && err.response.status >= 400 && err.response.status <= 499) {
            switch (typeContextMatch) {
              case CIS_USER_ID_CONTEXT:
              case MRN_ID_CONTEXT:
                removeFromLookupHistory(typeContextMatch, userId)
                break
              default:
                removeFromLookupHistory(USER_ID_CONTEXT, userId)
            }
          }
          error(
            t('user.errors.gettingUser', {
              defaultValue: `Error getting user: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
  }, [forward, userId, t, context, typeContextMatch, error])

  React.useEffect(() => {
    fetchCurrentUser()
  }, [fetchCurrentUser])

  return [currentUser, fetchCurrentUser]
}
